/**
 * REACT_APP_PREVIEW_URL already includes the `/preview` path prefix (e.g.
 * https://ontheweb.ai/preview), so only the slug needs to be appended here.
 */
export const getPreviewUrl = (slug: string): string =>
  `${process.env.REACT_APP_PREVIEW_URL || 'http://localhost:3003/preview'}/${slug}`;

/**
 * The live (published, non-preview) site is served at the same domain as
 * the preview, minus the `/preview` path segment, e.g.
 * https://ontheweb.ai/preview -> https://ontheweb.ai. REACT_APP_LIVE_URL
 * can be set explicitly to override this derivation if the two ever need
 * to point at different hosts.
 */
export const getLiveUrl = (slug: string, customDomain?: string | null): string => {
  if (customDomain) {
    return `https://${customDomain.replace(/^https?:\/\//, '').replace(/\/.*$/, '')}`;
  }
  const previewBase = process.env.REACT_APP_PREVIEW_URL || 'http://localhost:3003/preview';
  const liveBase = process.env.REACT_APP_LIVE_URL || previewBase.replace(/\/preview\/?$/, '');
  try {
    const url = new URL(liveBase);
    if (url.hostname === 'localhost' || url.hostname === '127.0.0.1') {
      return `${liveBase.replace(/\/$/, '')}/${slug}`;
    }
    const apex = url.hostname.replace(/^www\./, '');
    const port = url.port ? `:${url.port}` : '';
    return `${url.protocol}//${slug}.${apex}${port}`;
  } catch {
    return `${liveBase.replace(/\/$/, '')}/${slug}`;
  }
};

export const verifiedCustomDomain = (
  website?: {
    customDomain?: string | { hostname?: string; status?: string } | null;
    customDomainStatus?: string;
  } | null
): string | undefined => {
  if (!website) return undefined;
  if (website.customDomain && typeof website.customDomain === 'object') {
    return website.customDomain.status === 'verified' ? website.customDomain.hostname : undefined;
  }
  return website.customDomainStatus === 'verified' && typeof website.customDomain === 'string'
    ? website.customDomain
    : undefined;
};

interface ShareWebsiteParams {
  businessName: string;
  slug: string;
  hasPublishedVersion?: boolean;
  customDomain?: string | null;
}

/**
 * Shares the live site URL if the website has been published, otherwise
 * falls back to the preview URL. Uses the native share sheet when
 * available (mobile browsers), otherwise copies the link to the clipboard.
 * Returns which path was taken so callers can decide whether to show a
 * "Link copied" confirmation (no confirmation is needed after a native
 * share, since the OS already shows its own UI).
 */
export const shareWebsite = async ({ businessName, slug, hasPublishedVersion, customDomain }: ShareWebsiteParams): Promise<'shared' | 'copied'> => {
  const url = hasPublishedVersion ? getLiveUrl(slug, customDomain) : getPreviewUrl(slug);

  if (typeof navigator !== 'undefined' && typeof navigator.share === 'function') {
    try {
      await navigator.share({ title: businessName, url });
      return 'shared';
    } catch (err: any) {
      // AbortError means the user dismissed the native share sheet — that's
      // a deliberate cancellation, not a failure, so don't fall back to copy.
      if (err?.name === 'AbortError') return 'shared';
    }
  }

  await navigator.clipboard.writeText(url);
  return 'copied';
};
